import { useEffect, useRef } from 'react'
import Loading from '@/components/Loading'

/**
 * Host element for Paddle Billing's inline checkout iframe.
 *
 * Paddle finds its mount point by class name (`frameTarget: 'paddle-checkout'`
 * in `SignInManager.pay`) and `SignInManager` toggles loader/frame visibility by
 * the two element ids below. Both are therefore document-global: only one of
 * these may be mounted at a time. The paywall modal and the partner landing page
 * each render it in their own payment step, and those steps are mutually
 * exclusive.
 *
 * The loader is absolutely positioned, so the host must establish a positioning
 * context (`position: relative`).
 */
function PaddleCheckoutFrame({ onOpenCheckout }: { onOpenCheckout: (signal: AbortSignal) => void }) {
  // The checkout must open exactly once per mount. `onOpenCheckout` identity
  // churns as background Redux updates (profile refresh, post-checkout setUser)
  // rebuild the callback, and re-running it would spawn a second iframe.
  const openCheckoutRef = useRef(onOpenCheckout)
  openCheckoutRef.current = onOpenCheckout

  // Deferred a tick so the container below is in the DOM before Paddle looks
  // for it.
  useEffect(() => {
    const controller = new AbortController()
    const timeoutId = window.setTimeout(() => {
      openCheckoutRef.current(controller.signal)
    }, 0)
    return () => {
      controller.abort()
      window.clearTimeout(timeoutId)
    }
  }, [])

  // Tear the iframe down if React unmounts us mid-checkout, otherwise it
  // survives outside the React tree.
  useEffect(() => {
    return () => {
      try {
        // @ts-ignore Paddle Billing exposes Checkout.close at runtime
        window.Paddle?.Checkout?.close?.()
      } catch {
        /* ignore */
      }
    }
  }, [])

  return (
    <>
      <div
        id="paddle-checkout-loader"
        style={{
          display: 'none',
          position: 'absolute',
          bottom: '50%',
          left: '50%',
          transform: 'translate(-50%, 0%)',
          paddingLeft: '12px',
        }}
      >
        <Loading fill="#BCBCBC" />
      </div>
      <div id="paddle-checkout" className="paddle-checkout" style={{ width: '100%' }} />
    </>
  )
}

export default PaddleCheckoutFrame
